import React, {
  type ReactNode,
  cloneElement,
  isValidElement,
  useCallback,
} from "react";

import { Link } from "react-router-dom";

import { CircleQuestionMarkIcon } from "@lucide/icons";

import type { OpenAPI } from "simplydo/interfaces";
import { ExternalLinkModal } from "simplydo/react/components/ExternalLinkModal";
import { useTheme } from "simplydo/react/theme";
import { Icon } from "simplydo/react/ui/icon";
import { Popup } from "simplydo/react/ui/popup";

import { useAppSelector } from "store";

import { UserChip } from "./Chips";

const hostNameSplit = window.location.host.split(".");
const withoutSubdomain =
  hostNameSplit.slice(1).join(".") || window.location.host;
const localRegex = new RegExp(
  `((^| )(http://www.https://www.|http://|https://)(([a-z]{1,15}.)?${withoutSubdomain}(/| |$)))`,
  "gi",
);

export const regex =
  // @ts-ignore
  /\((?<type>[@#]|\$[a-z]+)\)\[(?<label>(?:[.a-z,!?<> '_#@0-9\u00C0-\u024F]|-|\(|\))+)\]\(_id:(?<id>[a-z0-9#]+)\)/gim;

// Complex URL regex found on web
const urlRegex =
  /(https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|www\.[a-zA-Z0-9][a-zA-Z0-9-]+[a-zA-Z0-9]\.[^\s]{2,}|https?:\/\/(?:www\.|(?!www))[a-zA-Z0-9]+\.[^\s]{2,}|www\.[a-zA-Z0-9]+\.[^\s]{2,})/gim;

const matchingBracketPairs: Record<string, string> = {
  "(": ")",
  "[": "]",
  "{": "}",
  "<": ">",
};

const stripMatchingOuterBrackets = (value: string): string => {
  const open = value[0];
  const close = open ? matchingBracketPairs[open] : undefined;
  if (close && value.length > 1 && value[value.length - 1] === close) {
    return value.slice(1, -1);
  }
  return value;
};

export const replaceString = "RegexSplitPolyfillFix4thMay2021";

type MentionMatch = RegExpExecArray & {
  groups: { type: string; label: string; id: string };
};

const hasMentionGroups = (match: RegExpExecArray): match is MentionMatch =>
  typeof match.groups?.type === "string" &&
  typeof match.groups?.label === "string" &&
  typeof match.groups?.id === "string";

type FormattedCommentProps = {
  children?: string | Array<string | ReactNode> | ReactNode;
  noLink?: boolean;
  mentionStyle?: React.CSSProperties;
  linkStyle?: React.CSSProperties;
  mentionedUsers?: Record<string, OpenAPI.Schemas["User"]>;
  mentionedUserAccess?: Record<string, boolean>;
  maxLength?: number;
};

const FormattedComment = ({
  children,
  noLink = false,
  mentionStyle,
  linkStyle,
  mentionedUsers,
  mentionedUserAccess,
  maxLength,
}: FormattedCommentProps) => {
  const theme = useTheme();
  const user = useAppSelector((state) => state.user);

  const truncateCommentPreservingMentions = useCallback(
    (comment: string) => {
      if (!maxLength || comment.length <= maxLength) {
        return comment;
      }

      const mentionRegex = new RegExp(regex.source, regex.flags);

      let cut = maxLength;
      let match = mentionRegex.exec(comment);

      while (match) {
        const start = match.index ?? 0;
        const end = start + match[0].length;

        if (start < cut && cut < end) {
          cut = end;
        }

        match = mentionRegex.exec(comment);
      }

      return comment.slice(0, cut).concat("...");
    },
    [maxLength],
  );

  const returnFormattedComment = useCallback(
    (comment: string): ReactNode[] => {
      const allMatches: MentionMatch[] = [];
      // const compiled = new RegExp(/\((?<type>[@#]|\$[a-z]+)\)\[(?<label>[.a-z \(\)\'\\-\_#@0-9\u00C0-\u024F]+)\]\(_id:(?<id>[a-z0-9#]+)\)/, 'gim')

      let match = regex.exec(comment);
      while (match !== null) {
        if (hasMentionGroups(match)) {
          allMatches.push(match);
        }
        match = regex.exec(comment);
      }

      // https://github.com/zloirock/core-js/issues/751
      // Using .split() with regex is broken with current Polyfill, so we replace in the comment with a random string
      // Then split on that

      // Mapped Replaced Comment is just the text, without the mentions
      const mappedReplacedComment = comment
        .replace(regex, replaceString)
        .split(replaceString);
      // Has Matches indicates if there are any mentions
      const hasMatches = allMatches.length;

      // If there are no matches, we don't need to do anything
      let commentWithMatches: Array<string | ReactNode> = mappedReplacedComment;
      if (hasMatches) {
        commentWithMatches = [];
        for (let i = 0; i < mappedReplacedComment.length; i += 1) {
          if (i > 0) {
            const matchItems = allMatches[i - 1];
            if (matchItems) {
              for (let y = 0; y < matchItems.length; y += 1) {
                if (y > 0) {
                  commentWithMatches.push(matchItems[y]);
                }
              }
            }
          }
          const text = mappedReplacedComment[i];
          commentWithMatches.push(text);
        }
      }

      return commentWithMatches.map((word, index) => {
        // Adjust index to ignore the 3 captured groups

        if (index % 4) return "";
        const adjustedIndex = index / 4;
        if (adjustedIndex < allMatches.length) {
          const matchGroups = allMatches[adjustedIndex]?.groups;
          if (!matchGroups) return word;
          const { type, label, id } = matchGroups;
          const linkTo = (() => {
            switch (type) {
              case "@":
                return "users";
              case "$ideas":
              case "$challenges":
              case "$groups":
                return type.slice(1);
              case "#":
                return "tags";
              default:
                return null;
            }
          })();

          if (!linkTo) {
            return word;
          }

          // If we specify whether a user can access a comment, then we know they can't, we display a helpful message so other commenters know
          const userCanAccess =
            mentionedUserAccess === undefined
              ? true
              : mentionedUserAccess?.[id];

          const mentionUser = mentionedUsers?.[id];

          return (
            <React.Fragment key={index}>
              {word}
              {mentionUser ? (
                <span
                  style={{
                    color:
                      user?._id === mentionUser?._id
                        ? theme.secondaryColour
                        : theme.primaryColour,
                  }}
                >
                  <UserChip user={mentionUser} compact="very" />
                </span>
              ) : (
                <Link
                  to={`/${linkTo}/${id}`}
                  style={
                    userCanAccess
                      ? linkStyle
                      : {
                          color: "gray",
                          opacity: 0.8,
                          textDecorationLine: "underline",
                        }
                  }
                >
                  {label}
                </Link>
              )}
              {!userCanAccess ? (
                <Popup
                  trigger={
                    <Icon
                      icon={CircleQuestionMarkIcon}
                      style={{
                        color: "gray",
                        opacity: 0.8,
                        height: 20,
                        marginLeft: 3,
                      }}
                    />
                  }
                  hoverable
                  on="hover"
                  position="top center"
                  content="This user has been mentioned, but is unable to access this page. They will not receive notification of this mention or any further comments."
                />
              ) : null}
            </React.Fragment>
          );
        }

        if (noLink || typeof word !== "string") return word;
        // Map remaining comment across newlines and whitespace, then test each word for a link and insert as necessary
        const splitSentence = word
          .split("\n")
          .map((sentence: string, i: number) =>
            sentence.split(" ").map((w: string, j: number) => {
              const padded = j === 0 ? "" : " ";
              const urlWord = stripMatchingOuterBrackets(w);
              // Without further research, there is an issue where a comment with multiple links after another would fail this check if the urlRegex is only tested once (???)
              // Therefore we just test twice and hope for the best
              if (urlRegex.test(urlWord) || urlRegex.test(urlWord)) {
                // Extract just the URL from the word (the word may have surrounding characters like punctuation)
                const extractedUrl = urlWord.match(urlRegex)?.[0] ?? urlWord;
                // Return URL as both hyperlink and the text
                const isLocal = extractedUrl.match(localRegex);
                if (isLocal) {
                  return (
                    <a
                      href={extractedUrl}
                      key={i + j}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={mentionStyle}
                    >
                      {padded}
                      {w}
                    </a>
                  );
                }

                return (
                  <ExternalLinkModal href={extractedUrl} key={i + j}>
                    {padded + w}
                  </ExternalLinkModal>
                );
              }
              return padded + w;
            }),
          );
        const fullResult: ReactNode[] = [];
        splitSentence.forEach((s: ReactNode[]) => {
          fullResult.push(s);
          fullResult.push("\n");
        });
        return fullResult;
      });
    },
    [
      noLink,
      mentionedUserAccess,
      linkStyle,
      mentionStyle,
      mentionedUsers,
      theme.primaryColour,
      theme.secondaryColour,
      user?._id,
    ],
  );

  const getFormattedComponent = (
    comment: string | Array<string | ReactNode> | ReactNode,
    index = 0,
  ): ReactNode => {
    if (typeof comment === "string") {
      const truncatedComment = truncateCommentPreservingMentions(comment);
      // / Base return of recursion, if it's a raw string we can split into words and handle any linkable
      return returnFormattedComment(truncatedComment);
    }
    if (Array.isArray(comment)) {
      // If it's an array of elements, we handle each element individually
      return comment.map((c, key) => getFormattedComponent(c, key));
    }
    if (isValidElement<{ children?: ReactNode }>(comment)) {
      return cloneElement(
        comment,
        { key: index },
        getFormattedComponent(comment.props.children),
      );
    }
    return null;
  };

  return getFormattedComponent(children);
};

export default FormattedComment;
